import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { APPRAISAL_KINDS } from '@/components/NewAppraisalKindButton';
import { QuoteTypeQuickPick } from '@/components/QuoteTypeQuickPick';
import { DatePickerField } from '@/components/DatePickerField';
import { createAppraisalWithDupeCheck, quoteTypeStatus, propertyLabel } from '@/utils/createAppraisal';

const today = () => format(new Date(), 'yyyy-MM-dd');

function KindCards({ kind, setKind }) {
  return (
    <div className="space-y-1.5" role="radiogroup" data-testid="appraisal-type-cards">
      {Object.entries(APPRAISAL_KINDS).map(([key, k]) => {
        const Icon = k.icon;
        const on = kind === key;
        return (
          <button key={key} type="button" role="radio" aria-checked={on} onClick={() => setKind(key)}
            data-testid={`appraisal-type-card-${key}`}
            className={`w-full px-3 py-2.5 text-left rounded-lg border-2 flex items-start gap-2.5 transition-colors ${on ? 'border-emerald-500 bg-emerald-50' : 'border-slate-100 hover:bg-slate-50'}`}>
            <div className="mt-0.5 p-1.5 rounded-md" style={{ backgroundColor: `${k.accent}15` }}><Icon size={14} style={{ color: k.accent }} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">{key === 'quote_only' ? 'Quote only (no appraisal report)' : k.actionLabel.replace(/^New /, '')}</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                {key === 'free_estimate' ? 'Opens the Free Appraisal form for this client + property (Google Earth measurements).'
                  : key === 'quote_only' ? 'General quote — the Quote Type is chosen later in the Workflow Runner.' : k.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PropertyPicker({ properties, value, onChange, onAddProperty }) {
  if (!properties.length) {
    return (
      <div data-testid="no-property-notice" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        This client has no property yet — add one first so the appraisal can be linked.
        {onAddProperty && (
          <button type="button" data-testid="no-property-add-btn" onClick={onAddProperty}
            className="mt-2 block text-xs font-semibold text-[#D35400] hover:text-[#A04000]">+ Add Property</button>
        )}
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 block mb-1">Which property? *</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="appraisal-property-select"><SelectValue placeholder="Choose a property…" /></SelectTrigger>
        <SelectContent>
          {properties.map(p => (
            <SelectItem key={p._id} value={p._id} data-testid={`appraisal-property-option-${p._id}`}>
              {propertyLabel(p) || `Property P${p.property_no || ''}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Shared "+ New Appraisal" + "Quote Type" buttons — Property and Client pages.
export function AppraisalStartButtons({ api, clientId, properties = [], fixedPropertyId = '', roofTypes = [], preferred = [], compact = false, testIdPrefix = 'property', onAddProperty }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [kind, setKind] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const autoPropId = fixedPropertyId || (properties.length === 1 ? properties[0]._id : '');
  const selectedProp = properties.find(p => p._id === propertyId);

  const create = async (pid, quote_type_ref, appraisal_status, appraisal_date) => {
    setBusy(true);
    try {
      const data = await createAppraisalWithDupeCheck(api, { client_id: clientId, property_id: pid, quote_type_ref, appraisal_status, appraisal_date });
      if (data) navigate(`/appraisals/${data._id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to create appraisal');
    } finally { setBusy(false); }
  };

  const openType = () => { setKind(''); setPropertyId(autoPropId); setDate(today()); setModal({ mode: 'type' }); };
  const onPickQt = (qt) => {
    if (autoPropId) { create(autoPropId, qt.ref, quoteTypeStatus(qt), today()); return; }
    setPropertyId(''); setDate(today()); setModal({ mode: 'qt', qt });
  };
  const addProperty = () => { setModal(null); onAddProperty?.(); };

  const confirm = () => {
    if (!propertyId) return;
    if (modal.mode === 'qt') { create(propertyId, modal.qt.ref, quoteTypeStatus(modal.qt), date); return; }
    if (kind === 'free_estimate') { navigate(`/free-appraisal?client_id=${clientId}&property_id=${propertyId}`); return; }
    const k = APPRAISAL_KINDS[kind];
    create(propertyId, k.quote_type_ref, k.appraisal_status, date);
  };

  const ready = !!propertyId && (modal?.mode === 'qt' || !!APPRAISAL_KINDS[kind]);
  const label = modal?.mode === 'qt' ? `Create ${modal.qt.ref}` : kind === 'free_estimate' ? 'Open Free Appraisal form' : APPRAISAL_KINDS[kind] ? `Create ${APPRAISAL_KINDS[kind].label}` : 'Pick an appraisal type';
  const size = compact ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';

  return (
    <>
      <button type="button" onClick={openType} data-testid={`${testIdPrefix}-new-appraisal-btn`}
        className={`flex items-center gap-1.5 ${size} bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold transition-colors shadow-sm`}>
        <Plus size={compact ? 12 : 14} strokeWidth={2.5} /> New Appraisal
      </button>
      <QuoteTypeQuickPick api={api} compact={compact} testIdPrefix={`${testIdPrefix}-qt-pick`}
        roofTypes={roofTypes} preferred={preferred} onPick={onPickQt} />
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) setModal(null); }}>
        <DialogContent data-testid="new-appraisal-modal" className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modal?.mode === 'qt' ? `New ${modal.qt.ref}` : 'Select Appraisal Type'}</DialogTitle>
            <DialogDescription data-testid="new-appraisal-property-label">
              {selectedProp ? propertyLabel(selectedProp) : 'Pick the property this appraisal belongs to.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!fixedPropertyId && <PropertyPicker properties={properties} value={propertyId} onChange={setPropertyId} onAddProperty={onAddProperty && addProperty} />}
            {modal?.mode === 'type' && <KindCards kind={kind} setKind={setKind} />}
            {kind !== 'free_estimate' && (
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Appraisal Date</label>
                <DatePickerField value={date} onChange={setDate} testId="appraisal-date-input" />
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button data-testid="create-appraisal-btn" onClick={confirm} disabled={!ready || busy}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed">
              {busy && <Loader2 size={14} className="animate-spin" />} {label}
            </button>
            <button onClick={() => setModal(null)} data-testid="cancel-btn" className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
